// SPDX-License-Identifier: AGPL-3.0-or-later

import { Link, useNavigate } from 'react-router';
import { BookmarkPlus, Eye, FilePlus } from 'lucide-react';
import { Button, useToast } from '@intakeform/ui';
import { useQuestionnaires, useSaveAsTemplate } from '../../api/questionnaires.js';

export function FormsRoute() {
  const forms = useQuestionnaires();
  const saveAsTemplate = useSaveAsTemplate();
  const { toast } = useToast();
  const navigate = useNavigate();

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">Forms</h1>
          <p className="text-sm text-muted-foreground mt-1">
            Manage and edit your published patient intake forms.
          </p>
        </div>
        <Button type="button" onClick={() => navigate('/builder')}>
          <FilePlus size={16} className="mr-2" />
          New form
        </Button>
      </div>

      {forms.isLoading ? (
        <p className="text-muted-foreground">Loading forms...</p>
      ) : forms.error ? (
        <div className="error-banner">Failed to load forms.</div>
      ) : (forms.data ?? []).length === 0 ? (
        <div className="text-center py-12 border border-dashed border-border rounded-xl">
          <p className="text-muted-foreground">No forms yet. Click “New form” to start.</p>
        </div>
      ) : (
        <div className="grid gap-3">
          {forms.data?.map((q) => (
            <div
              key={q.id}
              className="p-4 bg-surface border border-border rounded-xl flex items-center justify-between shadow-sm hover:border-primary-500 transition-all"
            >
              <div>
                <div className="font-semibold text-foreground text-base">
                  <Link to={`/builder/${q.id}`} className="hover:text-primary-600 hover:underline">
                    {q.title}
                  </Link>
                </div>
                <div className="text-xs text-muted-foreground mt-0.5">
                  Version {q.version} • Status: <span className="font-medium">{q.status}</span> •
                  Updated: {new Date(q.updatedAt).toLocaleDateString()}
                </div>
              </div>

              <div className="flex items-center gap-2">
                <Button
                  data-testid={`save-as-template-${q.id}`}
                  variant="ghost"
                  disabled={saveAsTemplate.isPending}
                  onClick={() => {
                    saveAsTemplate.mutate(q.id, {
                      onSuccess: (res) => {
                        const replaced = res.replacedTemplateId !== null;
                        toast({
                          title: replaced ? 'Template updated' : 'Saved as template',
                          description: replaced
                            ? `"${q.title}" replaced your prior template for this form.`
                            : `"${q.title}" is now available in your Templates library.`,
                          variant: 'success',
                        });
                      },
                      onError: (err) => {
                        if (err.code === 'template_content_duplicate') {
                          const existingId =
                            err.details !== null &&
                            typeof err.details === 'object' &&
                            'existingTemplateId' in err.details &&
                            typeof (err.details as { existingTemplateId: unknown })
                              .existingTemplateId === 'string'
                              ? (err.details as { existingTemplateId: string }).existingTemplateId
                              : null;
                          toast({
                            title: 'Already have a template with these questions',
                            description:
                              existingId !== null
                                ? `Open Templates to use the existing one (id ${existingId.slice(0, 8)}).`
                                : err.message,
                            variant: 'info',
                          });
                          return;
                        }
                        const failures =
                          err.code === 'template_constraints_failed' &&
                          err.details !== null &&
                          typeof err.details === 'object' &&
                          'failures' in err.details &&
                          Array.isArray((err.details as { failures: unknown }).failures)
                            ? (err.details as { failures: { message: string }[] }).failures
                            : null;
                        toast({
                          title: 'Cannot save as template',
                          description:
                            failures !== null && failures.length > 0
                              ? failures.map((f) => f.message).join(' ')
                              : err.message,
                          variant: 'danger',
                        });
                      },
                    });
                  }}
                >
                  <BookmarkPlus size={14} className="mr-1.5" />
                  Save as template
                </Button>
                <Button variant="secondary" onClick={() => navigate(`/preview/${q.id}`)}>
                  <Eye size={14} className="mr-1.5" />
                  Preview
                </Button>
                <Button variant="ghost" onClick={() => navigate(`/builder/${q.id}`)}>
                  Edit
                </Button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
